import { ChangeDetectionStrategy, Component, inject, Input, OnInit, signal } from "@angular/core";
import { EMPTY, switchMap } from "rxjs";
import { datadogRum } from "@datadog/browser-rum";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { ActivatedRoute, Data, Router } from "@angular/router";
import { DarkModeService } from "@core-services/dark-mode.service";
import { TranslationService } from "@core-services/translation.service";
import { containsString } from "@helpers/utils";
import { CustomModal } from "@modules/core/components/base-modals";
import { BpSelectOption } from "@modules/core/components/bp-select/bp-select.component";
import {
	AddressBookWalletTransactionData,
	DigitalSignatureProof,
	OnHoldVerificationStep,
	FlowAction,
	NotabeneConfigInterface,
	WalletVerificationData,
	WalletVerificationStatus
} from "@modules/main/components/address-book-wallet/model/address-book-wallet.model";
import { AddressBookWalletService } from "@modules/main/components/address-book-wallet/services/address-book-wallet.service";
import Notabene, {
	AgentType,
	Completed,
	Error,
	Deposit,
	EmbeddedComponent,
	PersonType,
	ProofTypes,
	TransactionOptions,
	Withdrawal
} from "@notabene/javascript-sdk";
import { config } from "@config";

@Component({
	selector: "app-verification-hold-modal",
	templateUrl: "./verification-hold-modal.component.html",
	styleUrl: "./verification-hold-modal.component.scss",
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class VerificationHoldModalComponent extends CustomModal implements OnInit {
	@Input() data: Data;
	public transactionData: AddressBookWalletTransactionData;
	private readonly darkModeService = inject(DarkModeService);
	private readonly walletService = inject(AddressBookWalletService);
	private readonly route = inject(ActivatedRoute);
	private readonly router = inject(Router);
	private readonly sanitizer = inject(DomSanitizer);

	private notabeneComponent: EmbeddedComponent<Deposit | Withdrawal, TransactionOptions> | null = null;
	private transactionId: string = "";
	private addressBookId: string = "";
	private walletVerificationData: WalletVerificationData | null = null;
	onHoldVerificationStep = OnHoldVerificationStep;
	verificationStep = signal<OnHoldVerificationStep>(OnHoldVerificationStep.Loading);
	private previousStep: OnHoldVerificationStep = OnHoldVerificationStep.OnHold;
	transactionAmountAndSymbol: string;
	mode: "dark" | "light" = this.darkModeService.darkModeEnabled ? "dark" : "light";
	verificationAction = signal<FlowAction>(FlowAction.DEPOSIT);

	warningInput = signal<SafeHtml | null>(null);

	walletAddress(): string {
		return this.transactionData.payment_data.wallet_address;
	}

	constructor() {
		super();
	}

	ngOnInit(): void {
		this.warningInputHelper();
		this.initForm();
	}

	private warningInputHelper(): void {
		const lang = this.translationService.getLanguage();
		const warningText = this.translationService.translate("digital_signature_warning_sheet_message");
		const supportURL = `${config.helpcenterUrl}hc/${lang === "deu" ? "de" : "en"}`;

		const match = warningText.match(/\\n(.+)$/);
		if (!match) {
			this.warningInput.set(this.sanitizer.bypassSecurityTrustHtml(warningText));
			return;
		}

		const lastText = match[1];
		const htmlLink = `&nbsp;<a href="${supportURL}" target="_blank" style="text-decoration: underline">${lastText}</a>`;
		const result = warningText.replace(/\\n(.+)$/, htmlLink);

		this.warningInput.set(this.sanitizer.bypassSecurityTrustHtml(result));
	}

	private initForm(): void {
		this.transactionId = this.route.snapshot.queryParams["transaction_id"] || this?.data?.transactionId;
		this.addressBookId = this.route.snapshot.queryParams["address_book_id"] || this?.data?.addressBookId;
		if (!this.transactionId) {
			return;
		}

		this.walletService
			.getPaymentTransaction$(this.transactionId)
			.pipe(
				switchMap((transaction: AddressBookWalletTransactionData) => {
					this.verificationAction.set(transaction.transaction_type as FlowAction);
					this.transactionData = transaction;
					this.transactionAmountAndSymbol = `${transaction.asset_amount} ${transaction.asset_symbol}`;
					const addressBookId = this.addressBookId || transaction.payment_data.address_book_entry_id;
					if (!addressBookId) {
						this.verificationStep.set(OnHoldVerificationStep.ErrorFailed);
						return EMPTY;
					}
					this.addressBookId = addressBookId;
					return this.walletService.getWalletVerificationData(addressBookId, this.transactionId);
				})
			)
			.subscribe({
				next: (walletVerificationData: WalletVerificationData) => {
					this.walletVerificationData = walletVerificationData;
					if (walletVerificationData.verification_status !== undefined) {
						switch (walletVerificationData.verification_status) {
							case WalletVerificationStatus.SUCCESSFUL:
								this.verificationStep.set(OnHoldVerificationStep.Success);
								break;
							case WalletVerificationStatus.FAILED:
								this.verificationStep.set(OnHoldVerificationStep.ErrorFailed);
								break;
							case WalletVerificationStatus.IN_PROGRESS:
								this.verificationStep.set(OnHoldVerificationStep.VerificationInProgress);
								break;
							case WalletVerificationStatus.REQUIRED:
								this.verificationStep.set(OnHoldVerificationStep.OnHold);
								break;
							default:
								break;
						}
					} else {
						this.verificationStep.set(OnHoldVerificationStep.OnHold);
					}
				},
				error: () => {
					this.verificationStep.set(OnHoldVerificationStep.ErrorPaymentTransactionNotFound);
				}
			});
	}

	public getHostSearch(): Function {
		return (searchInput: string, { name }: BpSelectOption): boolean => containsString(name, searchInput);
	}

	doItLater(): void {
		this.verificationStep.set(OnHoldVerificationStep.ExitWithoutVerifying);
	}

	verifyWallet(): void {
		this.handleNotabeneConfig();
	}

	notNow(): void {
		this.modalService.popAll();
		this.router.navigateByUrl("/");
	}

	continueVerification(): void {
		this.handleNotabeneConfig();
	}

	goToPortfolio(): void {
		this.modalService.popAll();
		this.router.navigateByUrl("/portfolio");
	}

	goToTransactionHistory(): void {
		this.modalService.popAll();
		this.router.navigateByUrl("/my-account/history");
	}

	backFromNotabene(): void {
		this.verificationStep.set(this.previousStep);
	}

	private notabeneConfigHandler(config: NotabeneConfigInterface): void {
		this.notabeneComponent?.removeEmbed();
		this.previousStep = this.verificationStep();
		this.verificationStep.set(OnHoldVerificationStep.Notabene);
		const notabene = new Notabene({
			nodeUrl: config.configuration.baseUrl,
			authToken: config.configuration.accessToken,
			theme: config.theme,
			locale: config.configuration.locale
		});

		const data = config.data;
		const options = config.notabeneOptions;
		let component: EmbeddedComponent<Deposit | Withdrawal, TransactionOptions>;

		if (this.verificationAction() === FlowAction.DEPOSIT) {
			component = notabene.createDepositAssist(data, options);
		}
		if (this.verificationAction() === FlowAction.WITHDRAWAL) {
			component = notabene.createWithdrawalAssist(data, options);
		}
		component.on("error", (error: Error) => {
			const identifier = error.type === "error" ? error.identifier : "unknown";
			datadogRum.addError("Notabene component: SDK Error: " + identifier, error);
		});
		this.notabeneComponent = component;
		component.mount("#notabene");
		component.on("complete", (result: Completed<Deposit | Withdrawal>) => {
			const isValid = result.response.valid;
			const proof = result.response.proof as DigitalSignatureProof | undefined;
			if (isValid === false || !proof) {
				this.verificationStep.set(OnHoldVerificationStep.ErrorFailed);
				return;
			}
			this.walletService.sendVerificationProof(this.addressBookId, proof).subscribe({
				next: () => this.verificationStep.set(OnHoldVerificationStep.VerificationInProgress),
				error: () => this.verificationStep.set(OnHoldVerificationStep.ErrorFailed)
			});
		});
	}

	private handleNotabeneConfig(): void {
		const walletVerificationData = this.walletVerificationData;
		if (!walletVerificationData) {
			this.verificationStep.set(OnHoldVerificationStep.ErrorFailed);
			return;
		}

		const config: NotabeneConfigInterface = {
			theme: {
				primaryColor: "#18632e",
				secondaryColor: "#000000",
				fontFamily: "Inter",
				mode: this.mode
			},
			configuration: {
				flow: this.verificationAction(),
				backgroundColor: this.darkModeService.darkModeEnabled ? "#000000" : "#ffffff",
				accessToken: walletVerificationData.customer_token,
				baseUrl: "https://api.eu1.notabene.id",
				locale: TranslationService.getLanguageIsoCode()
			},
			data: {
				asset: walletVerificationData.notabene_asset_id,
				...(this.verificationAction() === FlowAction.DEPOSIT && {
					source: this.walletAddress()
				}),
				...(this.verificationAction() === FlowAction.WITHDRAWAL && {
					destination: this.walletAddress()
				}),
				amountDecimal: Number(this.transactionData.asset_amount)
			},
			notabeneOptions: {
				proofs: {
					reuseProof: false,
					fallbacks: [ProofTypes.SelfDeclaration]
				},
				counterpartyAssist: {
					counterpartyTypes: [PersonType.SELF]
				},
				allowedAgentTypes: [AgentType.PRIVATE],
				allowedCounterpartyTypes: [PersonType.SELF],
				fields: {
					naturalPerson: {},
					legalPerson: {}
				}
			}
		};

		this.notabeneConfigHandler(config);
	}
}
